import React from "react";
import { Navigate, NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "../../lib/auth";
import {
  LayoutDashboard, BookOpen, Users, Newspaper, Star, Handshake, Image as ImageIcon,
  Wallet, Mail, GraduationCap, LogOut, ExternalLink, Video, Instagram, Megaphone, MessageCircle, Search, PenSquare,
} from "lucide-react";

const nav = [
  { to: "/admin", end: true, icon: LayoutDashboard, label: "Dashboard" },
  { to: "/admin/seo", icon: Search, label: "SEO & Settings" },
  { to: "/admin/courses", icon: BookOpen, label: "Courses" },
  { to: "/admin/banners", icon: ImageIcon, label: "Banners" },
  { to: "/admin/blog", icon: PenSquare, label: "Blog Posts" },
  { to: "/admin/ticker", icon: Megaphone, label: "News Ticker" },
  { to: "/admin/videos", icon: Video, label: "Videos" },
  { to: "/admin/instagram", icon: Instagram, label: "Instagram Feed" },
  { to: "/admin/faculty", icon: Users, label: "Faculty" },
  { to: "/admin/news", icon: Newspaper, label: "News & Events" },
  { to: "/admin/testimonials", icon: Star, label: "Testimonials" },
  { to: "/admin/partners", icon: Handshake, label: "Partners" },
  { to: "/admin/financial-aid", icon: Wallet, label: "Financial Aid" },
  { to: "/admin/contacts", icon: Mail, label: "Contact Inbox" },
  { to: "/admin/admissions", icon: GraduationCap, label: "Admissions" },
  { to: "/admin/chat-leads", icon: MessageCircle, label: "Chat Leads" },
];

export default function AdminLayout() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();

  if (loading) return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  if (!user) return <Navigate to="/admin/login" replace />;

  const handleLogout = () => {
    logout();
    navigate("/admin/login");
  };

  return (
    <div className="min-h-screen flex bg-slate-50">
      {/* Sidebar */}
      <aside className="w-64 shrink-0 bg-[#04102e] text-slate-300 flex flex-col">
        <div className="p-5 border-b border-white/10">
          <div className="flex items-center gap-2">
            <div className="h-10 w-10 rounded-xl bg-yellow-400 flex items-center justify-center">
              <GraduationCap className="h-6 w-6 text-[#0B2A6B]" strokeWidth={2.5} />
            </div>
            <div className="leading-none">
              <div className="text-white font-bold text-sm">Kiki Admin</div>
              <div className="text-[10px] text-yellow-400 tracking-widest mt-1">CONTROL PANEL</div>
            </div>
          </div>
        </div>
        <nav className="flex-1 p-3 overflow-y-auto">
          {nav.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm mb-1 transition ${
                  isActive
                    ? "bg-yellow-400 text-[#0B2A6B] font-bold"
                    : "hover:bg-white/5 hover:text-white"
                }`
              }
            >
              <n.icon className="h-4 w-4" />
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-white/10 space-y-1">
          <a
            href="/"
            target="_blank"
            rel="noreferrer"
            className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm hover:bg-white/5 hover:text-white"
          >
            <ExternalLink className="h-4 w-4" /> View Site
          </a>
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm hover:bg-red-500/15 hover:text-red-300"
          >
            <LogOut className="h-4 w-4" /> Logout
          </button>
        </div>
      </aside>

      <main className="flex-1 overflow-x-hidden">
        <header className="bg-white border-b border-slate-200 px-8 py-4 flex items-center justify-between">
          <h1 className="text-lg font-bold text-[#0B2A6B]">Admin Dashboard</h1>
          <div className="text-sm text-slate-500">
            Hello, <span className="font-semibold text-[#0B2A6B]">{user.username}</span>
          </div>
        </header>
        <div className="p-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
